import { get, map } from "lodash";
import { CanvasRenderConfig, getCanvasRender } from "./canvas-render";
import { useImageStore } from "entity-store/image-store/use-image-store";
import { SaveImageConfig } from "entity-store/image-store/use-image-store-remote";
import {
  mapRenderSizeFromPreset,
  fullRenderSizeFromPreset,
  PosterProject,
  GPSSource,
} from "PosterEditor/model/poster-project";
import { useAuth } from "BEDROCK/auth/supabase-auth";
import { MapStaticImageOptions } from "PosterEditor/model/poster-project";
import { mapboxLngLatBoundsFromCoordinateBounds } from "PosterEditor/hooks/use-coordinates";
import { useMapBoxUtils } from "PosterEditor/hooks/use-mapbox-utils";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { v4 as uuid } from "uuid";
import { useState } from "react";

export interface ProjectRenderConfig {
  project: PosterProject;
  map: React.MutableRefObject<any>;
  gpsSource: GPSSource | undefined;
}

export const useRenderProject = (authToken: string | undefined) => {
  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ?? "";
  const { project, setLastRenderID, markRendered } = usePosterProject();

  const { getStaticMapURL } = useMapBoxUtils(accessToken);
  const { saveImage, getImageUrl } = useImageStore();
  const [isRendering, setIsRendering] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean | undefined>();
  const [error, setError] = useState<boolean | undefined>();

  const renderProject = async (config: ProjectRenderConfig) => {
    setIsRendering(true);
    const container = config.map.current.getContainer();
    const rect = container.getBoundingClientRect();
    //TODO : fix this canvas cal witht th wrong confgig
    const canvasConfig: CanvasRenderConfig = {
      map: config.map,
      originalMapSize: { width: rect.width, height: rect.height },
      project: config.project,
      gpsSource: config.gpsSource,
    };

    const blob = await getCanvasRender(canvasConfig);
    // const url = URL.createObjectURL(blob);
    // const a = document.createElement("a");
    // a.href = url;
    // a.download = "canvas.png";
    // document.body.appendChild(a);
    // a.click();
    // document.body.removeChild(a);
    // URL.revokeObjectURL(url);
    const id = uuid();
    const saveConfig: SaveImageConfig = {
      image: blob,
      existingFilename: project.lastRenderID,
      newFilename: id,
      isPublic: false,
      folder: `renders/${config.project.id}`,
      mimeTypeParam: blob.type,
    };
    setLastRenderID(id);

    await saveImage(saveConfig);

    const mapBoxURL = mapBoxStaticURLFromProject(config.project);

    if (!mapBoxURL || !authToken) {
      setIsRendering(false);
      setSuccess(false);
      setError(true);
      console.error("MapBox URL or auth token is undefined");
      return;
    }
    const request: RenderFunctionRequest = {
      projectID: config.project.id,
      mapBoxURL: mapBoxURL,
      mapSize: mapRenderSizeFromPreset(
        config.project.size,
        config.project.borderStyle.borderWidth ?? 0
      ),
      posterSize: fullRenderSizeFromPreset(config.project.size),
      borderColor: config.project.borderStyle.borderColor ?? "white",
      canvasImageID: id,
      authToken: authToken,
    };

    await callRenderFunction(request);
    markRendered(true);
  };

  const mapBoxStaticURLFromProject = (project: PosterProject) => {
    if (!project.bounds) {
      return;
    }
    const bounds = mapboxLngLatBoundsFromCoordinateBounds(project.bounds);

    const mapURLParms: MapStaticImageOptions = {
      southwest: bounds.getSouthWest(),
      northeast: bounds.getNorthEast(),
      mapStyle: project.mapStyle,
      renderSizePreset: project.size,
      borderWidthMM: project.borderStyle.borderWidth ?? 0,
      isThumbnail: false,
    };
    const staticMapUrl = getStaticMapURL(mapURLParms);
    return staticMapUrl;
  };

  interface RenderFunctionRequest {
    projectID: string;
    mapBoxURL: string;
    mapSize: { width: number; height: number };
    posterSize: { width: number; height: number };
    borderColor: string;
    canvasImageID: string;
    authToken: string;
  }

  const callRenderFunction = async (params: RenderFunctionRequest) => {
    const border = Math.floor(
      (params.posterSize.width - params.mapSize.width) / 2
    );

    const url = process.env.REACT_APP_RENDER_URL!;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${params.authToken}`,
        },
        body: JSON.stringify({
          projectId: params.projectID,
          mapUrl: params.mapBoxURL,
          width: params.posterSize.width,
          height: params.posterSize.height,
          margin: border,
          marginColor: params.borderColor,
          canvasImageID: params.canvasImageID,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Render function failed:", {
          status: response.status,
          statusText: response.statusText,
          errorData,
        });
        setSuccess(false);
        setError(true);
      }
    } catch (err) {
      console.error(
        "Network or unexpected error calling render function:",
        err
      );
      setSuccess(false);
      setError(true);
    } finally {
      setSuccess(true);
      setIsRendering(false);
    }
  };

  return {
    renderProject,
    isRendering,
    error,
    success,
  };
};
